import { createTheme, ThemeOptions } from '@mui/material/styles';

// Core color palette - Flat UI design system
const colors = {
  // Primary - Green (financial success theme)
  primary: {
    main: '#22c55e',
    dark: '#16a34a',
    light: '#4ade80',
    contrastText: '#ffffff',
  },
  
  // Secondary - Neutral grays
  secondary: {
    main: '#6b7280',
    dark: '#4b5563',
    light: '#9ca3af',
    contrastText: '#ffffff',
  },
  
  // Semantic colors - consistent across theme modes
  success: {
    main: '#22c55e',
    dark: '#16a34a',
    light: '#4ade80',
  },
  
  error: {
    main: '#ef4444',
    dark: '#dc2626',
    light: '#f87171',
  },
  
  warning: {
    main: '#f59e0b',
    dark: '#d97706',
    light: '#fbbf24',
  },
  
  info: {
    main: '#3b82f6',
    dark: '#2563eb',
    light: '#60a5fa',
  },
  
  // Grey flat UI surfaces - no glass morphism
  flat: {
    // Dark mode grey flat surfaces
    dark: {
      primary: '#1a1a1a',      // Dark grey main background
      secondary: '#2a2a2a',    // Medium grey cards/sections
      tertiary: '#3a3a3a',     // Lighter grey hover states
      border: 'rgba(255, 255, 255, 0.1)',
      borderSecondary: 'rgba(255, 255, 255, 0.05)',
      hover: 'rgba(255, 255, 255, 0.02)',
      active: 'rgba(255, 255, 255, 0.05)',
    },
    // Light mode flat surfaces  
    light: {
      primary: '#ffffff',      // Pure white
      secondary: '#f8f9fa',    // Very light gray
      tertiary: '#f1f5f9',     // Light gray
      border: 'rgba(0, 0, 0, 0.1)',
      borderSecondary: 'rgba(0, 0, 0, 0.05)',
      hover: 'rgba(0, 0, 0, 0.01)',
      active: 'rgba(0, 0, 0, 0.02)',
    },
  },
  
  // Text colors - clear hierarchy
  text: {
    primary: {
      dark: '#ffffff',
      light: '#1d1d1f',
    },
    secondary: {
      dark: '#9ca3af',
      light: '#6b7280',
    },
    muted: {
      dark: '#6b7280',
      light: '#9ca3af',
    },
    disabled: {
      dark: '#4b5563',
      light: '#d1d5db',
    },
  },
  
  // Background colors - Grey flat UI system
  background: {
    default: {
      dark: '#1a1a1a',     // Dark grey (main background)
      light: '#ffffff',    // Pure white
    },
    paper: {
      dark: '#2a2a2a',     // Slightly lighter grey (cards/sections)
      light: '#f8f9fa',    // Very light gray
    },
    surface: {
      dark: '#212121',     // Surface grey (profile cards, content areas)
      light: '#f5f5f5',    // Light surface gray
    },
    elevated: {
      dark: '#3a3a3a',     // Even lighter grey (hover states)
      light: '#f1f5f9',    // Light gray
    },
  },
};

// Common transition configurations
const transitions = {
  standard: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  gentle: 'all 0.3s ease',
  hover: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

// Flat UI - NO shadows (keeping structure for compatibility)
const shadows = {
  none: 'none',
  glass: {
    light: 'none',
    dark: 'none',
  },
  hover: {
    light: 'none',
    dark: 'none',
  },
  modal: {
    light: 'none',
    dark: 'none',
  },
  elevated: {
    light: 'none',
    dark: 'none',
  },
};

// Base theme configuration
const createInsightTheme = (mode: 'light' | 'dark'): ThemeOptions => {
  const isDark = mode === 'dark';
  
  return {
    // Add custom properties
    customColors: colors,
    customTransitions: transitions,
    customShadows: shadows,
    
    palette: {
      mode,
      primary: colors.primary,
      secondary: colors.secondary,
      success: colors.success,
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
      
      text: {
        primary: isDark ? colors.text.primary.dark : colors.text.primary.light,
        secondary: isDark ? colors.text.secondary.dark : colors.text.secondary.light,
      },
      
      background: {
        default: isDark ? colors.background.default.dark : colors.background.default.light,
        paper: isDark ? colors.background.paper.dark : colors.background.paper.light,
        surface: isDark ? colors.background.surface.dark : colors.background.surface.light,
      },
      
      divider: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
    },
    
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      
      // Headings with consistent letter spacing
      h1: {
        fontWeight: 700,
        fontSize: '3rem',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2.5rem', 
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.2,
      },
      
      // Body text
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      
      // UI elements
      caption: {
        fontSize: '0.75rem',
        fontWeight: 500,
        letterSpacing: '0.5px',
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 600,
      },
      
      // Button text
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.25px',
      },
    },
    
    shape: {
      borderRadius: 6, // Default border radius (more subtle)
    },
    
    spacing: 8, // 8px base spacing unit
    
    components: {
      // Button component styles - Flat UI
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0, // Sharp corners
            textTransform: 'none',
            fontWeight: 600,
            transition: 'background-color 0.2s linear, color 0.2s linear',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          contained: {
            backgroundColor: colors.primary.main,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: colors.primary.dark,
              boxShadow: 'none',
            },
          },
          outlined: {
            borderColor: isDark ? colors.flat.dark.border : colors.flat.light.border,
            '&:hover': {
              backgroundColor: isDark ? colors.flat.dark.hover : colors.flat.light.hover,
              borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            },
          },
        },
      },
      
      // Card component styles - Grey Flat UI
      MuiCard: {
        styleOverrides: {
          root: {
            background: isDark 
              ? colors.flat.dark.secondary  // #2a2a2a - medium grey
              : colors.flat.light.secondary, // #f8f9fa - light grey
            border: isDark 
              ? `1px solid ${colors.flat.dark.border}` 
              : `1px solid ${colors.flat.light.border}`,
            borderRadius: 0, // Sharp corners
            boxShadow: 'none',
            // Removed hover effects for cleaner, more stable UI
            // transition: 'background-color 0.2s linear, border-color 0.2s linear',
            // '&:hover': {
            //   backgroundColor: isDark ? colors.flat.dark.tertiary : colors.flat.light.tertiary,
            //   borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            //   boxShadow: 'none',
            // },
          },
        },
      },
      
      // Paper component styles - Grey Flat UI
      MuiPaper: {
        styleOverrides: {
          root: {
            background: isDark 
              ? colors.flat.dark.secondary  // #2a2a2a - medium grey
              : colors.flat.light.secondary, // #f8f9fa - light grey
            border: isDark 
              ? `1px solid ${colors.flat.dark.border}` 
              : `1px solid ${colors.flat.light.border}`,
            borderRadius: 0, // Sharp corners
            boxShadow: 'none',
          },
        },
      },
      
      // Table component styles - Grey Flat UI
      MuiTable: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark 
                ? colors.flat.dark.primary   // #1a1a1a - dark grey
                : colors.flat.light.primary, // #ffffff - white
              fontWeight: 600,
              color: isDark ? colors.text.secondary.dark : colors.text.secondary.light,
              borderBottom: isDark 
                ? `2px solid ${colors.flat.dark.border}` 
                : `2px solid ${colors.flat.light.border}`,
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: isDark 
                ? colors.flat.dark.hover 
                : colors.flat.light.hover,
            },
          },
        },
      },
      
      // Text field styles - Flat UI
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark 
                ? colors.flat.dark.secondary 
                : colors.flat.light.secondary,
              borderRadius: 0, // Sharp corners
              transition: 'background-color 0.2s linear, border-color 0.2s linear',
              '&:hover': {
                backgroundColor: isDark 
                  ? colors.flat.dark.tertiary 
                  : colors.flat.light.tertiary,
              },
              '&.Mui-focused': {
                backgroundColor: isDark 
                  ? colors.flat.dark.tertiary 
                  : '#ffffff',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark 
                ? colors.flat.dark.border 
                : colors.flat.light.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'rgba(0, 0, 0, 0.2)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
      
      // Dialog styles - Flat UI
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: isDark 
              ? colors.flat.dark.secondary 
              : colors.flat.light.primary,
            borderRadius: 0, // Sharp corners
            border: isDark 
              ? `1px solid ${colors.flat.dark.border}` 
              : `1px solid ${colors.flat.light.border}`,
            boxShadow: 'none',
          },
        },
      },
      
      // Chip component styles - Flat UI
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0, // Sharp corners
            fontWeight: 600,
            border: 'none',
          },
          filled: {
            '&.MuiChip-colorSuccess': {
              backgroundColor: isDark ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
              color: colors.success.main,
              border: `1px solid ${colors.success.main}`,
            },
            '&.MuiChip-colorError': {
              backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
              color: colors.error.main,
              border: `1px solid ${colors.error.main}`,
            },
          },
        },
      },
      
      // Autocomplete styles - Flat UI
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            background: isDark 
              ? colors.flat.dark.secondary 
              : colors.flat.light.secondary,
            border: isDark 
              ? `1px solid ${colors.flat.dark.border}` 
              : `1px solid ${colors.flat.light.border}`,
            borderRadius: 0, // Sharp corners
            boxShadow: 'none',
          },
          option: {
            '&:hover': {
              backgroundColor: isDark 
                ? colors.flat.dark.hover 
                : colors.flat.light.hover,
            },
          },
        },
      },
    },
  };
};

// Export light and dark themes
export const lightTheme = createTheme(createInsightTheme('light'));
export const darkTheme = createTheme(createInsightTheme('dark'));

// Export theme factory function
export { createInsightTheme };

// Export color palette for use in components
export { colors, transitions, shadows as customShadows };

// Type augmentation for custom theme properties
declare module '@mui/material/styles' {
  interface Theme {
    customColors: typeof colors;
    customTransitions: typeof transitions;
    customShadows: typeof shadows;
  }

  interface ThemeOptions {
    customColors?: typeof colors;
    customTransitions?: typeof transitions;
    customShadows?: typeof shadows;
  }

  interface TypeBackground {
    surface: string;
  }
}